import React, { useState, useEffect, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { ToastContext } from '../../../services/contexts/toast-context';
import { useCreateProject, useUpdateProject, useUploadProjectCover } from '../../../services/api/projects-api';
import { useGetFullPortfolio } from '../../../services/api/users-api';
import Flex from '../../../components/shared-styles/flexi';

export default function ProjectFormDialog({ visible, onHide, project = null }) {
  const toastRef = useContext(ToastContext);
  const isEditing = !!project;

  // State
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'completed',
    skill_ids: [],
    new_skills: [],
    start_date: null,
    end_date: null,
    links: [],
    is_published: true,
  });
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [newSkillInput, setNewSkillInput] = useState('');

  // API Hooks
  const { data: portfolioData } = useGetFullPortfolio();
  const skillsData = portfolioData?.skills || [];
  const { mutate: createProject, isPending: isCreating } = useCreateProject();
  const { mutate: updateProject, isPending: isUpdating } = useUpdateProject();

  const statusOptions = [
    { label: 'Completed', value: 'completed' },
    { label: 'In Progress', value: 'in_progress' },
    { label: 'Archived', value: 'archived' },
  ];

  // Load project data when editing
  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        description: project.description || '',
        status: project.status || 'completed',
        skill_ids: project.skills?.map(s => s.id) || [],
        new_skills: [],
        start_date: project.start_date ? new Date(project.start_date) : null,
        end_date: project.end_date ? new Date(project.end_date) : null,
        links: project.links || [],
        is_published: project.is_published !== false,
      });
    } else {
      // Reset form for new project
      setFormData({
        title: '',
        description: '',
        status: 'completed',
        skill_ids: [],
        new_skills: [],
        start_date: null,
        end_date: null,
        links: [],
        is_published: true,
      });
    }
    setNewSkillInput('');
  }, [project]);

  const handleSubmit = () => {
    // Validation
    if (!formData.title.trim()) {
      toastRef.current.show({
        severity: 'warn',
        summary: 'Validation Error',
        detail: 'Project title is required',
        life: 3000,
      });
      return;
    }

    const payload = {
      ...formData,
      start_date: formData.start_date ? formData.start_date.toISOString().split('T')[0] : null,
      end_date: formData.end_date ? formData.end_date.toISOString().split('T')[0] : null,
    };

    if (isEditing) {
      updateProject(
        { projectId: project.id, data: payload },
        {
          onSuccess: () => {
            toastRef.current.show({
              severity: 'success',
              summary: 'Success',
              detail: 'Project updated successfully',
              life: 3000,
            });
            onHide();
          },
          onError: (error) => {
            toastRef.current.show({
              severity: 'error',
              summary: 'Error',
              detail: error.message || 'Failed to update project',
              life: 3000,
            });
          },
        }
      );
    } else {
      createProject(payload, {
        onSuccess: () => {
          toastRef.current.show({
            severity: 'success',
            summary: 'Success',
            detail: 'Project created successfully',
            life: 3000,
          });
          onHide();
        },
        onError: (error) => {
          toastRef.current.show({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'Failed to create project',
            life: 3000,
          });
        },
      });
    }
  };

  const handleAddLink = () => {
    if (newLink.title && newLink.url) {
      setFormData({
        ...formData,
        links: [...formData.links, { ...newLink }],
      });
      setNewLink({ title: '', url: '' });
    }
  };

  const handleRemoveLink = (index) => {
    const updatedLinks = [...formData.links];
    updatedLinks.splice(index, 1);
    setFormData({ ...formData, links: updatedLinks });
  };

  const skillOptions = skillsData.map(skill => ({
    label: skill.name,
    value: skill.id,
  }));

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={isEditing ? 'Edit Project' : 'Create New Project'}
      modal
      style={{ width: '700px', maxWidth: '95vw' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        {/* Title */}
        <div>
          <label htmlFor="title" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            Title *
          </label>
          <InputText
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="Enter project title"
            className="w-full"
          />
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            Description *
          </label>
          <InputTextarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of the project"
            rows={6}
            className="w-full"
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            Status
          </label>
          <Dropdown
            id="status"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.value })}
            options={statusOptions}
            className="w-full"
          />
        </div>

        {/* Skills */}
        <div>
          <label htmlFor="skills" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            Technologies/Skills
          </label>
          <MultiSelect
            id="skills"
            value={formData.skill_ids}
            onChange={(e) => setFormData({ ...formData, skill_ids: e.value })}
            options={skillOptions}
            placeholder="Select existing skills"
            maxSelectedLabels={5}
            className="w-full"
            style={{ marginBottom: '0.5rem' }}
          />
          
          {/* Add new skill */}
          <div style={{ marginTop: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem', display: 'block' }}>
              Or add new skills (separated by commas)
            </label>
            <Flex gap="0.5rem">
              <InputText
                value={newSkillInput}
                onChange={(e) => setNewSkillInput(e.target.value)}
                placeholder="e.g., React, Python, Docker"
                style={{ flex: 1 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const skills = newSkillInput.split(',').map(s => s.trim()).filter(s => s);
                    if (skills.length > 0) {
                      setFormData({ 
                        ...formData, 
                        new_skills: [...new Set([...formData.new_skills, ...skills])] 
                      });
                      setNewSkillInput('');
                    }
                  }
                }}
              />
              <Button
                icon="pi pi-plus"
                onClick={() => {
                  const skills = newSkillInput.split(',').map(s => s.trim()).filter(s => s);
                  if (skills.length > 0) {
                    setFormData({ 
                      ...formData, 
                      new_skills: [...new Set([...formData.new_skills, ...skills])] 
                    });
                    setNewSkillInput('');
                  }
                }}
                className="p-button-sm p-button-outlined"
                disabled={!newSkillInput.trim()}
                tooltip="Add skills"
              />
            </Flex>
            
            {/* Display new skills to be added */}
            {formData.new_skills.length > 0 && (
              <div style={{ marginTop: '0.5rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {formData.new_skills.map((skill, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      padding: '0.25rem 0.5rem',
                      background: '#e0f2fe',
                      color: '#0369a1',
                      borderRadius: '4px',
                      fontSize: '0.875rem',
                    }}
                  >
                    <span>{skill}</span>
                    <i
                      className="pi pi-times"
                      style={{ cursor: 'pointer', fontSize: '0.75rem' }}
                      onClick={() => {
                        const updated = formData.new_skills.filter((_, i) => i !== index);
                        setFormData({ ...formData, new_skills: updated });
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <Flex gap="1rem">
          <div style={{ flex: 1 }}>
            <label htmlFor="start_date" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              Start Date <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span>
            </label>
            <Calendar
              id="start_date"
              value={formData.start_date}
              onChange={(e) => setFormData({ ...formData, start_date: e.value })}
              dateFormat="yy-mm-dd"
              showIcon
              className="w-full"
            />
          </div>

          <div style={{ flex: 1 }}>
            <label htmlFor="end_date" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
              End Date <span style={{ color: '#6b7280', fontWeight: 400 }}>(optional)</span>
            </label>
            <Calendar
              id="end_date"
              value={formData.end_date}
              onChange={(e) => setFormData({ ...formData, end_date: e.value })}
              dateFormat="yy-mm-dd"
              showIcon
              className="w-full"
            />
          </div>
        </Flex>

        {/* Links */}
        <div>
          <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
            Project Links
          </label>
          
          {formData.links.map((link, index) => (
            <Flex key={index} gap="0.5rem" alignItems="center" style={{ marginBottom: '0.5rem' }}>
              <span style={{ flex: 1 }}>{link.title}: {link.url}</span>
              <Button
                icon="pi pi-times"
                className="p-button-sm p-button-text p-button-danger"
                onClick={() => handleRemoveLink(index)}
              />
            </Flex>
          ))}

          <Flex gap="0.5rem" style={{ marginTop: '0.5rem' }}>
            <InputText
              value={newLink.title}
              onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
              placeholder="Link title (e.g., GitHub)"
              style={{ flex: 1 }}
            />
            <InputText
              value={newLink.url}
              onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
              placeholder="URL"
              style={{ flex: 2 }}
            />
            <Button
              icon="pi pi-plus"
              onClick={handleAddLink}
              className="p-button-sm p-button-outlined"
              disabled={!newLink.title || !newLink.url}
            />
          </Flex>
        </div>

        {/* Published Checkbox */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Checkbox
            inputId="is_published"
            checked={formData.is_published}
            onChange={(e) => setFormData({ ...formData, is_published: e.checked })}
          />
          <label htmlFor="is_published" style={{ cursor: 'pointer' }}>
            Publish this project (visible in portfolio)
          </label>
        </div>

        {/* Actions */}
        <Flex gap="1rem" justify="flex-end" style={{ marginTop: '1rem' }}>
          <Button
            label="Cancel"
            className="p-button-text p-button-secondary"
            onClick={onHide}
          />
          <Button
            label={isEditing ? 'Update Project' : 'Create Project'}
            className="p-button-primary"
            loading={isCreating || isUpdating}
            onClick={handleSubmit}
          />
        </Flex>
      </div>
    </Dialog>
  );
}
